import React, { useContext, useMemo, useEffect } from "react"
import { Sidebar } from "./Sidebar"
import { Logo } from "./Logo"
import { Menu } from "./Menu"
import { MenuItem } from "./MenuItem"
import { Submenu } from "./SubMenu"
import { Link } from "react-router-dom"

import config from "../../config/config.json"
import { useTheme } from "@mui/styles"
import { useLocation } from "react-router-dom"
import useNavItems from "./useNavItems"
import { useWindowSize } from "usehooks-ts"
import { AuthContext } from "../../context/Auth/AuthContext"

// Componente para pré-carregar imagens
const ImagePreloader = ({ imgSources }) => {
    useEffect(() => {
        imgSources.forEach((src) => {
            if (src) {
                const img = new Image()
                img.src = src
            }
        })
    }, [imgSources])

    return null
}

// SideMenu otimizado com React.memo para evitar re-renderizações desnecessárias
const SideMenu = React.memo(({ drawerOpen }) => {
    const { user, handleLogout } = useContext(AuthContext)
    const { width } = useWindowSize()
    const { navItems } = useNavItems()
    const theme = useTheme()
    const location = useLocation()

    // Memoizar os cálculos das imagens do logo para evitar recálculos
    const logoImages = useMemo(() => {
        return {
            collapsed: config.system.logoCollapsed[theme.mode],
            expanded: config.system.logo[theme.mode],
        }
    }, [theme.mode])

    // Pré-carregamento das imagens do logo
    const preloadImages = useMemo(() => {
        return [
            config.system.logo?.light,
            config.system.logo?.dark,
            config.system.logoCollapsed?.light,
            config.system.logoCollapsed?.dark,
        ].filter(Boolean)
    }, [])

    // Memoizando o valor de isCollapse
    const isCollapse = useMemo(() => !drawerOpen, [drawerOpen])

    // Memoizando a largura da sidebar
    const sidebarWidth = useMemo(() => "300px", [])
    const sidebarCollapseWidth = useMemo(
        () => (width <= 640 ? 0 : "80px"),
        [width],
    )

    // Memoizando a imagem do logo que será exibida
    const currentLogoImg = useMemo(() => {
        return isCollapse ? logoImages.collapsed : logoImages.expanded
    }, [isCollapse, logoImages])

    return (
        <>
            <ImagePreloader imgSources={preloadImages} />
            <Sidebar
                width={sidebarWidth}
                collapsewidth={sidebarCollapseWidth}
                themeColor={theme.palette.secondary.main}
                textColor={theme.palette.textPrimary}
                isCollapse={isCollapse}
                userName={user.name}
                designation={user.email}
                onLogout={handleLogout}
            >
                <Logo
                    component={Link}
                    href="/"
                    isCollapse={isCollapse}
                    img={currentLogoImg}
                >
                    {config.system.name}
                </Logo>

                {navItems.map((item, i) => (
                    <Menu subHeading={item.subtitle} key={i}>
                        {item.items.map((menuItem, j) => {
                            if (menuItem.disable) return <div key={j} />

                            if (menuItem.children?.length) {
                                return (
                                    <Submenu
                                        title={menuItem.name}
                                        key={j}
                                        icon={menuItem.icon}
                                        isSidebarCollapsed={isCollapse}
                                    >
                                        {menuItem.children.map(
                                            (nestedMenuItem, k) => {
                                                if (nestedMenuItem.disable)
                                                    return <></>
                                                return (
                                                    <MenuItem
                                                        key={k}
                                                        icon={
                                                            nestedMenuItem.icon
                                                        }
                                                        link={
                                                            nestedMenuItem.path
                                                        }
                                                        isSelected={
                                                            location.pathname ===
                                                            nestedMenuItem.path
                                                        }
                                                    >
                                                        {nestedMenuItem.name}
                                                    </MenuItem>
                                                )
                                            },
                                        )}
                                    </Submenu>
                                )
                            }
                            return (
                                <MenuItem
                                    icon={menuItem.icon}
                                    key={menuItem.name}
                                    link={menuItem.path}
                                    component={Link}
                                    isSelected={
                                        location.pathname === menuItem.path
                                    }
                                    badge={menuItem.badge?.enabled}
                                    badgeContent={menuItem.badge?.content}
                                    badgeColor={menuItem.badge?.color}
                                >
                                    {menuItem.name}
                                </MenuItem>
                            )
                        })}
                    </Menu>
                ))}
            </Sidebar>
        </>
    )
})

// Definir displayName explicitamente para debugging
SideMenu.displayName = "SideMenu"

export default SideMenu
